// apps/web/src/hooks/use-navigation-history.ts

'use client';

import { useState, useCallback, useMemo } from 'react';
import { stripLocale } from '@/lib/navigation-config';
import { generateUUID } from '@comp-sys/shared';

const SESSION_ID_KEY = 'comp-sys-session-id';
const HISTORY_KEY_PREFIX = 'comp-sys-navigation-history-';
const MAX_HISTORY_SIZE = 20;

export interface NavigationHistoryItem {
  path: string;
  timestamp: number;
}

export interface UseNavigationHistoryOptions {
  maxHistorySize?: number;
}

export interface UseNavigationHistoryReturn {
  history: readonly NavigationHistoryItem[];
  pushPath: (path: string) => void;
  popPath: () => string | null;
  peekPath: () => string | null;
  clearHistory: () => void;
}

/**
 * Get or create a unique session ID for this browser tab.
 * This ensures each tab has its own navigation history.
 */
function getSessionId(): string {
  if (typeof window === 'undefined') return '';

  let sessionId = sessionStorage.getItem(SESSION_ID_KEY);
  if (!sessionId) {
    sessionId = generateUUID();
    sessionStorage.setItem(SESSION_ID_KEY, sessionId);
  }
  return sessionId;
}

/**
 * Get the storage key for this session's navigation history.
 */
function getStorageKey(): string {
  return `${HISTORY_KEY_PREFIX}${getSessionId()}`;
}

/**
 * Clean up orphaned session history keys from previous tabs.
 */
function cleanupOrphanedSessions(): void {
  if (typeof window === 'undefined') return;

  const currentSessionId = sessionStorage.getItem(SESSION_ID_KEY);
  const keysToRemove: string[] = [];

  for (let i = 0; i < sessionStorage.length; i++) {
    const key = sessionStorage.key(i);
    if (key?.startsWith(HISTORY_KEY_PREFIX)) {
      const keySessionId = key.replace(HISTORY_KEY_PREFIX, '');
      if (keySessionId !== currentSessionId) {
        keysToRemove.push(key);
      }
    }
  }

  keysToRemove.forEach(key => sessionStorage.removeItem(key));
}

/**
 * Get navigation history from session storage.
 */
function getStoredHistory(): NavigationHistoryItem[] {
  if (typeof window === 'undefined') return [];

  try {
    const stored = sessionStorage.getItem(getStorageKey());
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}

/**
 * Save navigation history to session storage.
 */
function setStoredHistory(history: NavigationHistoryItem[]): void {
  if (typeof window === 'undefined') return;

  try {
    sessionStorage.setItem(getStorageKey(), JSON.stringify(history));
  } catch (e) {
    console.error('Failed to save navigation history:', e);
  }
}

/**
 * Hook to manage navigation history in session storage.
 * Each browser tab maintains its own history for isolation.
 */
export function useNavigationHistory(
  options: UseNavigationHistoryOptions = {}
): UseNavigationHistoryReturn {
  const { maxHistorySize = MAX_HISTORY_SIZE } = options;

  // Initialize state from session storage
  const [history, setHistory] = useState<NavigationHistoryItem[]>(() => {
    cleanupOrphanedSessions();
    return getStoredHistory();
  });

  const pushPath = useCallback((path: string) => {
    // Strip locale prefix before storing
    const pathWithoutLocale = stripLocale(path);

    setHistory(prev => {
      // Don't add if same as last path (circular/reload detection)
      if (prev[prev.length - 1]?.path === pathWithoutLocale) {
        return prev;
      }

      // Limit history size
      const trimmedHistory = prev.length >= maxHistorySize
        ? prev.slice(-maxHistorySize + 1)
        : prev;

      const newHistory = [
        ...trimmedHistory,
        { path: pathWithoutLocale, timestamp: Date.now() }
      ];

      setStoredHistory(newHistory);
      return newHistory;
    });
  }, [maxHistorySize]);

  const popPath = useCallback((): string | null => {
    let result: string | null = null;

    setHistory(prev => {
      if (prev.length <= 1) {
        // Nothing to pop or only one item (no previous page)
        result = null;
        return prev;
      }

      const newHistory = prev.slice(0, -1);
      setStoredHistory(newHistory);
      // Return the new top of stack (previous page)
      result = newHistory[newHistory.length - 1]?.path ?? null;
      return newHistory;
    });

    return result;
  }, []);

  const peekPath = useCallback((): string | null => {
    return history[history.length - 1]?.path ?? null;
  }, [history]);

  const clearHistory = useCallback(() => {
    setHistory([]);
    setStoredHistory([]);
  }, []);

  return useMemo(() => ({
    history,
    pushPath,
    popPath,
    peekPath,
    clearHistory,
  }), [history, pushPath, popPath, peekPath, clearHistory]);
}
